import React from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Container, Message } from 'semantic-ui-react';

interface FileInputProps {
    onFileSelected: (file: File) => void;
}

const FileInput: React.FC<FileInputProps> = ({ onFileSelected }) => {
    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept: { 'application/pdf': ['.pdf'] },
        maxSize: 10 * 1024 * 1024, // 10MB
        maxFiles: 1,
        onDrop: (files: File[]) => {
            if (files.length > 0) {
                onFileSelected(files[0]);
            }
        },
        validator: (file: File) => {
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                return {
                    code: 'invalid-file-type',
                    message: 'Only PDF files are allowed',
                };
            }
            return null;
        },
    });

    const hasFileTooLarge = fileRejections.some((rejection: FileRejection) =>
        rejection.errors.some((error) => error.code === 'file-too-large'),
    );

    const hasTooManyFiles = fileRejections.some((rejection: FileRejection) =>
        rejection.errors.some((error) => error.code === 'too-many-files'),
    );

    return (
        <Container
            className="file-input-container cursor-pointer"
            {...getRootProps({ isFocused, isDragAccept, isDragReject })}
        >
            <input {...getInputProps()} />
            <p>Select your syllabus file</p>
            <p>Only PDF files are accepted</p>
            <p>Maximum file size: 10MB</p>

            {acceptedFiles.length > 0 && (
                <div>
                    <strong>Selected file:</strong>
                    {acceptedFiles.map((file) => (
                        <div key={file.name}>{file.name}</div>
                    ))}
                </div>
            )}

            {hasFileTooLarge && (
                <Message negative>
                    <Message.Header>File Too Large</Message.Header>
                    <p>Please select a file smaller than 10MB.</p>
                </Message>
            )}

            {hasTooManyFiles && (
                <Message negative>
                    <Message.Header>Too Many Files</Message.Header>
                    <p>Please select only one file.</p>
                </Message>
            )}
        </Container>
    );
};

export default FileInput;
